import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useLens, lensStore } from "@/lib/lensStore";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Stethoscope, Users, TrendingUp, Landmark, Calculator, FlaskConical, Scale, BookOpen, Crosshair, Building2,
  Bot, ShieldCheck, AlertTriangle, CheckCircle2, Eye, Link2, Clock, Target, Sparkles, Pencil,
} from "lucide-react";

const FUNCTION_META = {
  "Medical Affairs": { icon: Stethoscope, desc: "Scientific narrative, education and testing-pathway leadership" },
  "Field Medical / MSL": { icon: Users, desc: "Field scientific exchange and practical management support" },
  "Commercial / Brand": { icon: TrendingUp, desc: "Brand positioning, messaging and institutional entrenchment" },
  "Market Access": { icon: Landmark, desc: "HTA engagement, reimbursement and diagnostic coverage" },
  "HEOR": { icon: Calculator, desc: "Health economics, outcomes research and value modeling" },
  "Clinical Development": { icon: FlaskConical, desc: "Trial strategy, combinations and paradigm-defining studies" },
  "Regulatory": { icon: Scale, desc: "Label strategy and regional regulatory positioning" },
  "Publications / Med Comms": { icon: BookOpen, desc: "Evidence dissemination and scientific framing" },
  "Competitive Intelligence": { icon: Crosshair, desc: "Threat monitoring, launch clocks and guideline surveillance" },
  "Executive Leadership": { icon: Building2, desc: "Portfolio decisions, investment sequencing and franchise posture" },
};

const STATUS_META = {
  "AI-generated draft": { icon: Bot, cls: "bg-violet-50 text-violet-700 border-violet-300 border-dashed", dot: "#7C3AED" },
  "Under scientific review": { icon: Eye, cls: "bg-sky-50 text-sky-700 border-sky-200", dot: "#0284C7" },
  "Expert validated": { icon: ShieldCheck, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "#059669" },
  "Revision required": { icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "#D97706" },
  "Approved for reporting": { icon: CheckCircle2, cls: "bg-teal-600 text-white border-teal-600", dot: "#0D9488" },
};

const NEXT_STATUSES = {
  "AI-generated draft": ["Under scientific review"],
  "Under scientific review": ["Expert validated", "Revision required"],
  "Expert validated": ["Approved for reporting", "Revision required"],
  "Revision required": ["Under scientific review"],
  "Approved for reporting": ["Revision required"],
};

const PRIORITY_CLS = {
  Critical: "bg-red-50 text-red-700 border-red-200",
  High: "bg-orange-50 text-orange-700 border-orange-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-slate-50 text-slate-600 border-slate-200",
};

const confColor = (c) => (c >= 85 ? "#059669" : c >= 70 ? "#0D9488" : "#D97706");

function ImplicationCard({ imp, onStatusChange, onEdited }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(imp.recommended_action);
  const sm = STATUS_META[imp.review_status] || STATUS_META["AI-generated draft"];
  const StatusIcon = sm.icon;
  const isDraft = imp.review_status === "AI-generated draft";
  const saveEdit = () => {
    api.patch(`/admin/implications/${imp.id}`, { recommended_action: text }).then((r) => {
      onEdited(r.data);
      setEditing(false);
      toast.success("Implication updated (audit-logged)");
    }).catch((e) => toast.error(e.response?.data?.detail || "Edit failed"));
  };
  return (
    <div data-testid={`implication-card-${imp.id}`}
      className={`bg-white border rounded-xl p-5 shadow-sm shadow-slate-100 ${isDraft ? "border-violet-300 border-dashed" : "border-slate-200/80"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className="text-[10px] font-medium bg-amber-50 text-amber-700 border-amber-200" data-testid={`prototype-rec-tag-${imp.id}`}>Prototype recommendation</Badge>
          <Badge variant="outline" className={`text-[10px] font-semibold ${PRIORITY_CLS[imp.priority]}`} data-testid={`priority-badge-${imp.id}`}>
            {imp.priority} priority
          </Badge>
          <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">
            <Clock className="w-3 h-3 mr-1" />{imp.urgency}
          </Badge>
        </div>
        <Badge variant="outline" className={`shrink-0 text-[10px] font-semibold ${sm.cls}`} data-testid={`review-status-badge-${imp.id}`}>
          <StatusIcon className="w-3 h-3 mr-1" />{imp.review_status}
        </Badge>
      </div>

      {editing ? (
        <div className="mt-3 space-y-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
            className="w-full text-sm border border-slate-200 rounded-lg p-2 font-bold text-slate-900" data-testid={`edit-implication-input-${imp.id}`} />
          <div className="flex gap-2">
            <button onClick={saveEdit} className="text-[10px] font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md px-2.5 py-1" data-testid={`save-implication-edit-${imp.id}`}>Save</button>
            <button onClick={() => { setText(imp.recommended_action); setEditing(false); }} className="text-[10px] font-medium text-slate-600 bg-slate-100 rounded-md px-2.5 py-1">Cancel</button>
          </div>
        </div>
      ) : (
        <h4 className="text-sm font-bold text-slate-900 leading-snug mt-3 group cursor-text" data-testid={`recommended-action-${imp.id}`}
          onDoubleClick={() => setEditing(true)} title="Double-click to edit (admin)">
          {imp.recommended_action}
          <button onClick={() => setEditing(true)} className="ml-1.5 align-middle opacity-40 hover:opacity-100" data-testid={`edit-implication-btn-${imp.id}`}>
            <Pencil className="w-3 h-3 inline text-slate-400" />
          </button>
        </h4>
      )}

      <div className="mt-3 space-y-2.5">
        <div className="border-l-2 border-indigo-500 pl-3">
          <p className="font-mono text-[9px] uppercase tracking-widest text-indigo-600 mb-0.5 flex items-center gap-1"><Sparkles className="w-3 h-3" />Driving intelligence</p>
          <p className="text-xs text-slate-600 leading-relaxed">{imp.driving_intelligence}</p>
        </div>
        <div className="border-l-2 border-emerald-500 pl-3">
          <p className="font-mono text-[9px] uppercase tracking-widest text-emerald-600 mb-0.5 flex items-center gap-1"><Target className="w-3 h-3" />Expected value</p>
          <p className="text-xs text-slate-600 leading-relaxed">{imp.expected_value}</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 mt-3.5">
        <div className="min-w-0">
          <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400">Target stakeholder</p>
          <p className="text-xs font-semibold text-slate-700 truncate">{imp.stakeholder}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">Confidence {imp.confidence}%</p>
          <div className="w-28 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${imp.confidence}%`, backgroundColor: confColor(imp.confidence) }} />
          </div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100">
        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-1"><Link2 className="w-3 h-3" />Linked sources</p>
        <div className="flex flex-wrap gap-1.5">
          {imp.sources.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1 text-[10px] bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 text-slate-600">
              <span className="font-mono text-[9px] uppercase tracking-wide text-slate-400">{s.type}</span>·<span>{s.label}</span>
            </span>
          ))}
        </div>
        {imp.eligibility_gate && (
          <p className={`text-[10px] mt-1.5 ${imp.eligibility_gate.passed ? "text-slate-500" : "text-red-600 font-semibold"}`} data-testid={`eligibility-gate-${imp.id}`}>
            <span className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 mr-1">Eligibility gate</span>
            {imp.eligibility_gate.eligible_items} eligible · {imp.eligibility_gate.blocked_items} blocked — {imp.eligibility_gate.note}
          </p>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-start justify-between gap-3">
        <div className="min-w-0 text-[10px] text-slate-500 leading-relaxed">
          {imp.reviewer ? (
            <>
              <span className="font-semibold text-slate-600">{imp.reviewer}</span>
              {imp.review_note && <span> — {imp.review_note}</span>}
            </>
          ) : (
            <span className="text-violet-600 font-medium flex items-center gap-1"><Bot className="w-3 h-3 shrink-0" />Awaiting scientific review — not for external use</span>
          )}
          <span className="block text-slate-400 mt-0.5">Generated {imp.generated_date} · Updated {imp.last_updated}</span>
        </div>
        <div className="shrink-0 w-48">
          <Select value="" onValueChange={(v) => onStatusChange(imp.id, v)}>
            <SelectTrigger className="h-7 text-[11px]" data-testid={`status-transition-trigger-${imp.id}`}>
              <SelectValue placeholder="Move to…" />
            </SelectTrigger>
            <SelectContent>
              {(NEXT_STATUSES[imp.review_status] || []).map((s) => (
                <SelectItem key={s} value={s} className="text-xs" data-testid={`status-option-${imp.id}-${s.toLowerCase().replace(/\W+/g, "-")}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default function Implications({ role }) {
  const [items, setItems] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const { functionFilter: fnFilter } = useLens();
  const setFnFilter = lensStore.setFunctionFilter;
  const [prFilter, setPrFilter] = useState("all");
  const [stFilter, setStFilter] = useState("all");
  const [roleView, setRoleView] = useState(null);

  useEffect(() => {
    api.get("/implications", { params: role?.id ? { role: role.id } : {} }).then((r) => {
      setItems(r.data.implications);
      setFunctions(r.data.functions || []);
      setStatuses(r.data.review_statuses || []);
      setRoleView(r.data.role_view);
    });
  }, [role?.id]);

  const onStatusChange = (id, status) => {
    api.patch(`/implications/${id}/status`, { status }).then((r) => {
      setItems((prev) => prev.map((i) => (i.id === id ? r.data : i)));
      toast.success(`Moved to "${status}"`);
    }).catch(() => toast.error("Status update failed"));
  };

  const filtered = useMemo(() => items.filter((i) =>
    (fnFilter === "all" || i.function === fnFilter) &&
    (prFilter === "all" || i.priority === prFilter) &&
    (stFilter === "all" || i.review_status === stFilter)), [items, fnFilter, prFilter, stFilter]);

  const relevant = role?.implicationFunctions || [];
  const fnOrder = [...functions].sort((a, b) => {
    const ar = relevant.includes(a) ? 0 : 1;
    const br = relevant.includes(b) ? 0 : 1;
    return ar - br;
  });

  const statusCounts = statuses.map((s) => ({ status: s, count: items.filter((i) => i.review_status === s).length }));
  const avgConf = items.length ? Math.round(items.reduce((a, i) => a + i.confidence, 0) / items.length) : 0;

  return (
    <div className="space-y-5" data-testid="implications-view">
      {/* AI governance banner */}
      <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="ai-governance-banner">
        <Bot className="w-4 h-4 text-violet-600 shrink-0 mt-0.5" />
        <p className="text-xs text-violet-800 leading-relaxed">
          <span className="font-semibold">AI-drafted, expert-governed.</span> Every implication begins as an AI-generated draft synthesized from the linked intelligence and must pass scientific review before it can be approved for reporting. AI drafts are never presented as final recommendations.
        </p>
      </div>

      {roleView && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 px-4 py-3" data-testid="role-lens-banner">
          <p className="text-xs text-slate-200">
            <span className="font-mono text-[9px] uppercase tracking-widest text-teal-400 mr-2">Role lens · {role?.label}</span>
            {roleView.functions.length > 0 ? (
              <>
                <span className="font-bold text-teal-300" data-testid="role-lens-matching-count">{roleView.matching_count}</span> of {roleView.total} governed implications match your functions ({roleView.functions.join(", ")}).
                {roleView.matching_count === 0 && <span className="text-amber-300 font-semibold"> No records currently match this lens — nothing is hidden.</span>}
                {" "}All {roleView.total} records remain visible below; matching groups are pinned first.
              </>
            ) : (
              <>This lens has no function mapping — showing the full governed register of {roleView.total} implications.</>
            )}
          </p>
        </div>
      )}

      {/* Pipeline stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="review-pipeline-stats">
        <div className="bg-white border border-slate-200/80 rounded-xl p-3.5 shadow-sm">
          <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400">Implications</p>
          <p className="font-heading text-xl font-bold text-slate-900 mt-0.5">{items.length}</p>
          <p className="text-[10px] text-slate-500">avg confidence {avgConf}%</p>
        </div>
        {statusCounts.map(({ status, count }) => {
          const sm = STATUS_META[status];
          const Icon = sm.icon;
          return (
            <button key={status} onClick={() => setStFilter(stFilter === status ? "all" : status)}
              data-testid={`status-stat-${status.toLowerCase().replace(/\W+/g, "-")}`}
              className={`text-left bg-white border rounded-xl p-3.5 shadow-sm transition-colors ${stFilter === status ? "border-teal-400 ring-1 ring-teal-100" : "border-slate-200/80 hover:border-slate-300"}`}>
              <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 flex items-center gap-1">
                <Icon className="w-3 h-3" style={{ color: sm.dot }} />{status}
              </p>
              <p className="font-heading text-xl font-bold mt-0.5" style={{ color: sm.dot }}>{count}</p>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="implications-filters">
        <Select value={fnFilter} onValueChange={setFnFilter}>
          <SelectTrigger className="h-8 w-56 text-xs" data-testid="function-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All functions</SelectItem>
            {functions.map((f) => <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={prFilter} onValueChange={setPrFilter}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All priorities</SelectItem>
            {["Critical", "High", "Medium"].map((p) => <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stFilter} onValueChange={setStFilter}>
          <SelectTrigger className="h-8 w-52 text-xs" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All review statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {(fnFilter !== "all" || prFilter !== "all" || stFilter !== "all") && (
          <button onClick={() => { setFnFilter("all"); setPrFilter("all"); setStFilter("all"); }}
            className="text-xs text-teal-600 hover:text-teal-700 font-medium" data-testid="clear-filters-btn">Clear filters</button>
        )}
      </div>

      {/* Function sections */}
      {fnOrder.filter((fn) => filtered.some((i) => i.function === fn)).map((fn) => {
        const meta = FUNCTION_META[fn] || FUNCTION_META["Medical Affairs"];
        const Icon = meta.icon;
        const fnItems = filtered.filter((i) => i.function === fn);
        const isRelevant = relevant.includes(fn);
        return (
          <div key={fn} data-testid={`function-section-${fn.toLowerCase().replace(/\W+/g, "-")}`}>
            <div className="flex items-center gap-2.5 mb-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${isRelevant ? "bg-teal-50 border-teal-200" : "bg-slate-50 border-slate-200"}`}>
                <Icon className={`w-4.5 h-4.5 ${isRelevant ? "text-teal-600" : "text-slate-500"}`} />
              </div>
              <div>
                <h3 className="font-heading text-base font-bold text-slate-900 flex items-center gap-2">
                  {fn}
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 font-normal">{fnItems.length} implication(s)</span>
                  {isRelevant && <Badge variant="outline" className="text-[9px] bg-teal-50 text-teal-700 border-teal-200">Priority for your role</Badge>}
                </h3>
                <p className="text-[11px] text-slate-500">{meta.desc}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              {fnItems.map((imp) => <ImplicationCard key={imp.id} imp={imp} onStatusChange={onStatusChange}
                onEdited={(u) => setItems((prev) => prev.map((i) => (i.id === u.id ? u : i)))} />)}
            </div>
          </div>
        );
      })}
      {filtered.length === 0 && items.length > 0 && (
        <p className="text-sm text-slate-500 text-center py-10" data-testid="no-results-message">No implications match the current filters.</p>
      )}
    </div>
  );
}
